import { useEffect, useState } from 'react';

// Third party
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

// Hooks
import useAuth from 'hooks/useAuth';
import useScriptRef from 'hooks/useScriptRef';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';

// Components
import { buttonStyle } from 'components/buttons/buttonStyle';
import Loader from 'components/Loader';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { FieldError } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Separator } from 'components/ui/separator';
import { PasswordInput } from './PasswordInput';

// Utils
import { parseHash } from 'utils/linkUtil';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/localStorage';
import { generateRandomString } from 'utils/randomGenerator';

// assets
import MicrosoftSignInBtn from 'assets/images/auth/microsoft_signin_light.svg?react';

// ============================|| AWS CONNITO - LOGIN ||============================ //

interface AzureJwt {
    nonce: string;
    preferred_username: string;
}

interface LoginFormValues {
    email: string;
    password: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const shouldHideForgotPassword = import.meta.env.VITE_SHOULD_HIDE_FORGOT_PASSWORD === 'true';

const AuthLogin = () => {
    const [checked, setChecked] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in render gate `if (isLoading) return <Loader />`
    const [isLoading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { login, azureLogin } = useAuth();
    const scriptedRef = useScriptRef();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormValues>({
        mode: 'onBlur',
        defaultValues: { email: '', password: '' },
    });

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const azureSSOLogin = async () => {
        try {
            setLoading(true);
            const nonce = getLocalStorage('sso_nonce');
            const hashValues = parseHash(location.hash);
            const idToken = hashValues['id_token'];

            // Nonce is not set or already removed
            if (!nonce) {
                return;
            }

            // Decode JWT without verifying signature
            const decodedToken = jwtDecode<AzureJwt>(idToken);
            const tokenNonce = decodedToken.nonce;
            const preferredUsername = decodedToken.preferred_username.toLowerCase();

            if (!tokenNonce || nonce !== tokenNonce) {
                throw new Error('Nonce mismatch');
            }

            //process azure jwt token to map oid to the employee and sync/update the email
            await axios.post(import.meta.env.VITE_API_URL + '/v1/auth/sso/azure', { idToken });

            removeLocalStorage('sso_nonce');
            await azureLogin(preferredUsername, idToken);
        } catch (e) {
            console.error('error: ', e);
            errorAlert('Error logging in with Microsoft SSO');
            navigate('/login');
        }
    };

    useEffect(() => {
        // Check if the page was redirected from the external SSO login page
        if (location.pathname === '/login/sso/azure') {
            azureSSOLogin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const getAzureSSOUrl = () => {
        // Generate nonce to validate the response
        const nonce = generateRandomString(15);
        const state = 'azure';
        const redirectUri = encodeURIComponent(`${window.location.origin}/login/sso/azure`);
        setLocalStorage('sso_nonce', nonce);

        // Save returnTo URL to sessionStorage before SSO redirect
        // React Router state is lost during external navigation
        const locationState = location.state as { returnTo?: string } | null;
        if (locationState?.returnTo) {
            sessionStorage.setItem('sso_return_to', locationState.returnTo);
        }

        return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${import.meta.env.VITE_AZURE_CLIENT_ID}&response_type=code%20id_token&redirect_uri=${redirectUri}&response_mode=fragment&scope=openid%20profile%20offline_access%20https%3A%2F%2Fgraph.microsoft.com%2Fuser.read&state=${state}&nonce=${nonce}`;
    };

    const onSubmit = async (values: LoginFormValues) => {
        setSubmitError(null);
        const trimmedPassword = values.password?.trim?.() ?? values.password;
        try {
            await login(values.email, trimmedPassword);
        } catch (err) {
            console.error(err);
            if (scriptedRef.current && err instanceof Error) {
                errorAlert(err.message);
                setSubmitError(err.message);
            }
        }
    };

    const emailRegister = register('email', {
        required: t('auth.email-required'),
        maxLength: { value: 255, message: t('auth.email-too-long') },
        pattern: { value: EMAIL_REGEX, message: t('auth.must-be-valid-email') },
    });
    const passwordRegister = register('password', {
        required: t('auth.password-required'),
        maxLength: { value: 255, message: t('auth.password-too-long') },
    });

    if (isLoading) {
        return <Loader />;
    }

    // Don't show the login form if the page was redirected from the external SSO login page
    if (location.pathname === '/login/sso/azure') {
        return null;
    }

    return (
        <form
            onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
            }}
            noValidate
        >
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <Label htmlFor="email-login">Email Address</Label>
                    <Input
                        autoComplete="on"
                        id="email-login"
                        type="email"
                        placeholder="Enter email address"
                        aria-invalid={Boolean(errors.email)}
                        {...emailRegister}
                    />
                    {errors.email && <FieldError>{errors.email.message}</FieldError>}
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor="password-login">Password</Label>
                    <PasswordInput
                        autoComplete="on"
                        id="password-login"
                        placeholder="Enter password"
                        aria-invalid={Boolean(errors.password)}
                        visible={showPassword}
                        onToggleVisible={handleClickShowPassword}
                        {...passwordRegister}
                        onBlur={(event) => {
                            setValue('password', event.target.value?.trim(), { shouldValidate: true });
                            void passwordRegister.onBlur(event);
                        }}
                    />
                    {errors.password && <FieldError>{errors.password.message}</FieldError>}
                </div>

                <div className="-mt-2 flex items-center justify-between gap-4">
                    <Label className="text-h6 cursor-pointer">
                        <Checkbox checked={checked} onCheckedChange={(value) => setChecked(value)} name="checked" />
                        Keep me signed in
                    </Label>
                    {!shouldHideForgotPassword && (
                        <RouterLink
                            to="/forgot-password"
                            className="text-h6 text-fg hover:text-link no-underline hover:underline"
                        >
                            <FormattedMessage id="auth.forgot-password" />
                        </RouterLink>
                    )}
                </div>
                {submitError && <FieldError>{submitError}</FieldError>}
                <Button
                    {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'w-full')}
                    disabled={isSubmitting}
                    type="submit"
                >
                    <FormattedMessage id="auth.login" />
                </Button>
            </div>
            <div>
                <div className="text-muted-foreground my-6 flex items-center gap-3 text-xs uppercase">
                    <Separator className="flex-1" />
                    <FormattedMessage id="auth.or" />
                    <Separator className="flex-1" />
                </div>
                <div className="flex justify-center">
                    <button
                        type="button"
                        className="cursor-pointer"
                        onClick={() => window.location.replace(getAzureSSOUrl())}
                    >
                        <MicrosoftSignInBtn />
                    </button>
                </div>
            </div>
        </form>
    );
};

export default AuthLogin;
